import React, { useCallback, useMemo, useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  Button,
  Stack,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { AdminDialog } from 'components/AdminDialog'
import { StatusChip } from 'components/StatusChip'
import { TOUCH_MIN } from 'core/theme.tokens'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { OtwAgGridReact as AgGridReact } from '@/components/AgGrid/OtwAgGridReact'
import { ColDef } from 'ag-grid-community'
import { useAdminListBuilder } from 'hooks/useAdminListBuilder'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import DeleteIcon from '@mui/icons-material/Delete'
import { downloadShareableInventoryPdf } from 'core/inventory/downloadShareableInventoryPdf'
import { useTheme } from '@mui/material/styles'
import { ValueFormatterParams, ICellRendererParams, RowClassParams } from 'ag-grid-community'
import { ShareDialog } from './ShareDialog'
import { HorizScrollBox } from 'components/HorizScrollBox'
import { MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import EmailIcon from '@mui/icons-material/Email'
import { useUsers } from 'hooks/useUsers'
import { InventoryList } from '@otw/models/inventory'
import { useInventory } from 'hooks/inventory/useInventory'
import { FreeTechLogoLoading } from 'components/FreeTechLogoLoading'
import { AdminPageFrame } from 'components/AdminPageFrame'
import { useIsMobile } from 'hooks/screen/useIsMobile'


const LoadingDialog = ({ open, message }: { open: boolean; message: string }) => (
  <AdminDialog open={open} onClose={() => {}}>
    <DialogContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>{message}</Typography>
      </Box>
    </DialogContent>
  </AdminDialog>
)

const DeleteListDialog = ({
  open,
  list,
  onClose,
  onConfirm,
  loading,
}: {
  open: boolean
  list: InventoryList | null
  onClose: () => void
  onConfirm: () => Promise<void>
  loading: boolean
}) => (
  <AdminDialog open={open} onClose={onClose} maxWidth='sm' fullWidth fullScreen={useIsMobile()}>
    <DialogTitle sx={{ color: 'error.main' }}>Delete List</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant='h6'>Are you sure you want to delete "{list?.name}"?</Typography>
        <Typography color='text.secondary'>
          This list contains {list?.inventoryIds.length} vehicles and is shared with{' '}
          {list?.associatedUserIds?.length || 0} users.
        </Typography>
        <Typography color='error' variant='body2'>
          This action cannot be undone. The list will be permanently deleted for all users.
        </Typography>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <Button onClick={onConfirm} color='error' variant='contained' disabled={loading}>
        {loading ? 'Deleting...' : 'Delete List'}
      </Button>
    </DialogActions>
  </AdminDialog>
)

const AdminListBuilder = () => {
  const {
    lists,
    loading: listsLoading,
    isError: listsError,
    error: listsErrorDetail,
    deleteList,
    updateListAssociations,
  } = useAdminListBuilder()
  const { vehicles } = useInventory('master_inventory')
  const [expandedList, setExpandedList] = useState<string | null>(null)
  const theme = useTheme()
  const isMobile = useIsMobile()
  const [selectedListForSharing, setSelectedListForSharing] = useState<InventoryList | null>(null)
  const { users, isLoading, isError, error } = useUsers()
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadMessage, setDownloadMessage] = useState('')
  const [listToDelete, setListToDelete] = useState<InventoryList | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const handleAccordionChange = useCallback(
    (listId: string) => {
      setExpandedList(expandedList === listId ? null : listId)
    },
    [expandedList],
  )

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: 'stock_number',
        headerName: 'Stock #',
        width: 120,
        filter: false,
        cellStyle: {
          fontWeight: '500',
          color: theme.palette.primary.main,
        } as any,
      },
      {
        field: 'year',
        headerName: 'Year',
        width: 90,
        filter: false,
      },
      {
        field: 'make',
        headerName: 'Make',
        width: 120,
        filter: false,
      },
      {
        field: 'model',
        headerName: 'Model',
        width: 120,
        filter: false,
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 120,
        filter: false,
      },
      {
        field: 'miles',
        headerName: 'Miles',
        width: 120,
        filter: false,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value ? `${Number(params.value).toLocaleString()} mi` : '',
        cellStyle: { textAlign: 'right' } as any,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        filter: false,
        cellRenderer: (params: ICellRendererParams) => (
          <Chip
            label={params.value || 'N/A'}
            size='small'
            color={
              params.value === 'Available'
                ? 'success'
                : params.value === 'Pending Sale'
                  ? 'warning'
                  : params.value === 'Sold'
                    ? 'error'
                    : 'default'
            }
            sx={{ minWidth: 85, justifyContent: 'center' }}
          />
        ),
      },
      {
        field: 'optic_list_price',
        headerName: 'List Price',
        width: 120,
        filter: false,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value ? `$${Number(params.value).toLocaleString()}` : '',
        cellStyle: {
          textAlign: 'right',
          fontWeight: '500',
          color: theme.palette.success.main,
        } as any,
      },
    ],
    [theme],
  )

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: false,
      suppressMovable: true,
      suppressMenu: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
      },
    }),
    [],
  )

  const getListVehicles = useCallback(
    (list: InventoryList) => {
      if (!vehicles) return []
      return vehicles.filter(vehicle => list.inventoryIds.includes(vehicle.id))
    },
    [vehicles],
  )

  const getRowStyle = useCallback(
    (params: RowClassParams) => {
      if (params.node.rowIndex === null) return {}

      return {
        backgroundColor: params.node.rowIndex % 2 === 0 ? theme.palette.background.default : 'transparent',
      }
    },
    [theme],
  )

  const handleDownloadPdf = useCallback(async (list: InventoryList) => {
    try {
      setDownloadMessage('Generating PDF...')
      setIsDownloading(true)
      await downloadShareableInventoryPdf('master_inventory', list.inventoryIds)
    } finally {
      setIsDownloading(false)
    }
  }, [])

  const handleDeleteList = useCallback(async () => {
    if (!listToDelete) return

    try {
      setDeleteLoading(true)
      await deleteList(listToDelete.id)
      setListToDelete(null)
    } finally {
      setDeleteLoading(false)
    }
  }, [listToDelete, deleteList])

  if (isLoading || listsLoading) {
    return <FreeTechLogoLoading />
  }

  if (isError) {
    return <div>Error: {error?.message}</div>
  }

  if (listsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color='error'>
          Error loading lists: {listsErrorDetail instanceof Error ? listsErrorDetail.message : 'Unknown error'}
        </Typography>
      </Box>
    )
  }

  return (
    <AdminPageFrame title='List Builder'>
    <Box>
      <LoadingDialog open={isDownloading} message={downloadMessage} />

      <DeleteListDialog
        open={!!listToDelete}
        list={listToDelete}
        onClose={() => setListToDelete(null)}
        onConfirm={handleDeleteList}
        loading={deleteLoading}
      />

      <Grid container spacing={3}>
        {lists.map(list => (
          <Grid item xs={12} key={list.id}>
            <Card
              elevation={1}
              sx={{
                '&:hover': {
                  boxShadow: 3,
                  transition: 'box-shadow 0.3s ease-in-out',
                },
              }}
            >
              <CardContent>
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  justifyContent='space-between'
                  alignItems={isMobile ? 'stretch' : 'center'}
                  spacing={isMobile ? 2 : 0}
                  mb={2}
                >
                  <Box>
                    <Typography variant='h6' sx={{ fontWeight: 500 }}>
                      {list.name}
                      {list.isArchived && (
                        <StatusChip tone='neutral' label='Archived' dense sx={{ ml: 1, display: 'inline-flex', verticalAlign: 'middle' }} />
                      )}
                    </Typography>
                    <Stack direction='row' spacing={2} alignItems='center'>
                      <Typography color='text.secondary' variant='body2'>
                        Created: {list.dateCreated ? new Date(list.dateCreated).toLocaleDateString('en-US') : 'N/A'}
                      </Typography>
                      <Typography color='text.secondary' variant='body2'>
                        •
                      </Typography>
                      <Typography color='text.secondary' variant='body2'>
                        Vehicles: {list.inventoryIds.length}
                      </Typography>
                    </Stack>
                    {list.description && (
                      <Typography color='text.secondary' variant='body2' sx={{ mt: 1 }}>
                        {list.description}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction={isMobile ? 'column' : 'row'} spacing={1} sx={{ width: isMobile ? '100%' : 'auto' }}>
                    <Button
                      startIcon={<PictureAsPdfIcon />}
                      variant='outlined'
                      size={isMobile ? 'medium' : 'small'}
                      fullWidth={isMobile}
                      onClick={() => handleDownloadPdf(list)}
                      disabled={isDownloading}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        ...(isMobile ? { minHeight: TOUCH_MIN } : {}),
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      }}
                    >
                      {isDownloading ? 'Generating...' : 'Download PDF'}
                    </Button>
                    <Button
                      startIcon={<PersonAddIcon />}
                      variant='outlined'
                      size={isMobile ? 'medium' : 'small'}
                      fullWidth={isMobile}
                      onClick={() => setSelectedListForSharing(list)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        minHeight: isMobile ? TOUCH_MIN : undefined,
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      }}
                    >
                      Share
                    </Button>
                    <Button
                      startIcon={<DeleteIcon />}
                      variant='outlined'
                      size={isMobile ? 'medium' : 'small'}
                      fullWidth={isMobile}
                      color='error'
                      onClick={() => setListToDelete(list)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        minHeight: isMobile ? TOUCH_MIN : undefined,
                        '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' },
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Stack>

                <Stack direction='row' spacing={2} alignItems='center' sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize='small' color='action' />
                    <Typography variant='body2' color='text.secondary'>
                      Shared with:
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {list.associatedUserIds?.length ? (
                      list.associatedUserIds.map(userId => {
                        const user = Array.isArray(users) ? users.find(u => u.id === userId) : null
                        return user ? (
                          <StatusChip key={userId} tone='neutral' label={user.email || 'User'} dense />
                        ) : null
                      })
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        No users
                      </Typography>
                    )}
                  </Box>
                </Stack>

                {selectedListForSharing && (
                  <ShareDialog
                    open={!!selectedListForSharing}
                    onClose={() => setSelectedListForSharing(null)}
                    list={selectedListForSharing}
                    users={users || []}
                    onShare={async (userIds: string[]) => {
                      if (!selectedListForSharing) return
                      await updateListAssociations({
                        listId: selectedListForSharing.id,
                        userIds,
                      })
                      setSelectedListForSharing(null)
                    }}
                  />
                )}

                <Accordion
                  expanded={expandedList === list.id}
                  onChange={() => handleAccordionChange(list.id)}
                  elevation={0}
                  sx={{
                    '&:before': { display: 'none' },
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      borderRadius: 1,
                    }}
                  >
                    <Typography>View Vehicles</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {isMobile ? (
                      <Box sx={{ p: 1.5 }}>
                        <MobileRecordList
                          isEmpty={getListVehicles(list).length === 0}
                          empty='No vehicles in this list'
                        >
                          {getListVehicles(list).map(vehicle => (
                            <MobileRecordCard
                              key={vehicle.id}
                              title={[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                              meta={vehicle.stock_number}
                              detail={vehicle.vin}
                            />
                          ))}
                        </MobileRecordList>
                      </Box>
                    ) : (
                    <HorizScrollBox>
                    <div style={{ height: 'auto', width: '100%', maxHeight: 480 }}>
                      <AgGridReact
                        rowData={getListVehicles(list)}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        pagination={true}
                        paginationPageSize={8}
                        domLayout='autoHeight'
                        enableRangeSelection={false}
                        enableCellTextSelection={false}
                        suppressPaginationPanel={false}
                        rowStyle={{
                          cursor: 'default',
                        }}
                        headerHeight={48}
                        rowHeight={44}
                        suppressLoadingOverlay={true}
                        suppressNoRowsOverlay={true}
                        overlayNoRowsTemplate='No vehicles in this list'
                        className={theme.palette.mode === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
                      />
                    </div>
                    </HorizScrollBox>
                    )}
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {lists.length === 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography align='center' color='textSecondary'>
                  No lists have been created yet.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
    </AdminPageFrame>
  )
}

export default AdminListBuilder
